import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from '@bancobarigui/barihaus';

import { bankUserService } from '@/services/bankUser.service';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { useAlertContext } from '@/contexts/alert.context';
import { cleanPhoneNumber } from '@/shared/utils/phoneHelpers';

export function useBankUserEdit() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { notify } = useAlertContext();

  const loadUserData = async () => {
    if (!userId) {
      notify({
        type: 'error',
        message: 'ID do usuário não fornecido',
      });
      navigate('/bank-list');
      return;
    }

    setLoading(true);
    try {
      const user = await bankUserService.getBankUserById(userId);

      form.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phoneNumber,
        roles: user.roles?.map(r => r.id) || [],
        inspectionTypes: user.inspectionTypes?.map(it => it.id) || [],
      });
    } catch (error) {
      handleError(error, 'Erro ao carregar dados do usuário');
      navigate('/bank-list');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!userId) return;

    setSubmitting(true);
    try {
      const cleanPhone = cleanPhoneNumber(values.phone);

      await bankUserService.updateBankUser(userId, {
        email: values.email,
        name: values.name,
        phoneNumber: cleanPhone,
        inspectionTypeIds: values.inspectionTypes || [],
        roleIds: values.roles || [],
      });

      notify({
        type: 'success',
        message: 'Usuário atualizado com sucesso!',
      });
      navigate('/bank-list');
    } catch (error) {
      handleError(error, 'Erro ao atualizar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/bank-list');
  };

  useEffect(() => {
    loadUserData();
  }, [userId]);

  return {
    form,
    loading,
    submitting,
    handleSubmit,
    handleCancel,
  };
}
